import React, { useCallback, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { Cart } from '../../models/Cart';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';

interface CartAgGridTableProps {
  carts: Cart[];
  isLoading?: boolean;
  isFetching?: boolean; // 🎯 Control de opacidad durante fetch
  className?: string;
  // 🎯 Props para handlers de acciones
  onCartEdit?: (cart: Cart) => void;
  onCartDelete?: (cart: Cart) => void;
  onCartAdjust?: (cart: Cart) => void;
  onCartPrint?: (cart: Cart, size: 'small' | 'large') => void;
  onCartToggleLock?: (cart: Cart) => void;
  onCartViewProducts?: (cart: Cart) => void;
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (
    sorting: { field: string; direction: 'asc' | 'desc' } | null
  ) => void;
}

export const CartAgGridTable = React.memo(function CartAgGridTable({
  carts,
  isFetching = false,
  onCartEdit,
  onCartDelete,
  onCartAdjust,
  onCartPrint,
  onCartToggleLock,
  onCartViewProducts,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
}: CartAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => {}, []);

  // 🎯 Usar el hook para manejar las columnas (con cellRenderers incluidos)
  const { filteredColumnDefs } = useAgGridColumns({
    onCartEdit,
    onCartDelete,
    onCartAdjust,
    onCartPrint,
    onCartToggleLock,
    onCartViewProducts,
  });

  // 🎯 Las columnas ya vienen con sus cellRenderers desde el hook
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  return (
    <div
      className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <AgGridTable
        ref={agGridRef}
        data={carts}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 150px)"
        enableSelection={false} // 🎯 Carritos no requiere selección múltiple
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}} // Pasamos un objeto vacío para evitar conflictos
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
});
